import { useLayoutEffect, useRef, useState } from 'react';

import type { SplitableHeights } from './pageDelimitationMeasure';

/** Reads header + item heights from a section via data-splitable-* (getBoundingClientRect only). */
function readSplitableHeights(sectionEl: HTMLElement, gapPx: number): SplitableHeights | null {
  const headerEl = sectionEl.querySelector<HTMLElement>('[data-splitable-header]');
  const itemEls = sectionEl.querySelectorAll<HTMLElement>('[data-splitable-item]');
  if (!headerEl || itemEls.length === 0) return null;

  const headerHeight = headerEl.getBoundingClientRect().height;
  const itemRects = Array.from(itemEls).map(el => el.getBoundingClientRect());
  const separatorContributions = Array.from(itemEls).map(el => {
    const separatorEl = el.querySelector<HTMLElement>('[data-splitable-separator]');
    if (!separatorEl) {
      return 0;
    }

    const separatorRect = separatorEl.getBoundingClientRect();
    const separatorMarginTop = Number.parseFloat(getComputedStyle(separatorEl).marginTop) || 0;
    return separatorRect.height + separatorMarginTop;
  });
  const itemHeights = itemRects.map((rect, index) =>
    Math.max(0, rect.height - (separatorContributions[index] ?? 0)),
  );
  const measuredItemGaps = itemRects
    .slice(0, -1)
    .map((rect, index) => Math.max(0, itemRects[index + 1]!.top - rect.bottom));
  const separatorGapPx =
    separatorContributions.slice(0, -1).length > 0
      ? separatorContributions.slice(0, -1).reduce((sum, current) => sum + current, 0) /
        separatorContributions.slice(0, -1).length
      : 0;
  const itemGapPx =
    measuredItemGaps.length > 0
      ? measuredItemGaps.reduce((sum, current) => sum + current, 0) / measuredItemGaps.length +
        separatorGapPx
      : gapPx;
  const itemGaps =
    measuredItemGaps.reduce((sum, current) => sum + current, 0) +
    separatorContributions.slice(0, -1).reduce((sum, current) => sum + current, 0);
  const totalItems = itemHeights.reduce((a, b) => a + b, 0);
  const containerHeight = sectionEl.getBoundingClientRect().height;
  const footerHeight = Math.max(0, containerHeight - headerHeight - totalItems - itemGaps);
  return { headerHeight, itemHeights, itemGapPx, footerHeight };
}

export interface UseListSplittablesOptions {
  gapPx: number;
  splitableSections: [number, number][];
  sectionCountPerColumn: number[];
  /** When this changes, splitable data is reset so we re-read for new content. */
  contentKey?: string | number;
  /** When container is inside a scaled transform, convert to logical pixels (divide by scale). */
  scale?: number;
}

/**
 * Lists splitable sections and reads their heights from the DOM (querySelector + getBoundingClientRect).
 * No layout calculation, only DOM reading.
 */
export function useListSplittables(
  sectionRefsRef: React.RefObject<(HTMLElement | null)[][]>,
  options: UseListSplittablesOptions,
  enabled: boolean,
): Map<string, SplitableHeights> | null {
  const { gapPx, splitableSections, sectionCountPerColumn, contentKey, scale = 1 } = options;
  const [splitableByKey, setSplitableByKey] = useState<Map<string, SplitableHeights> | null>(null);
  const prevContentKeyRef = useRef<string | number | undefined>(undefined);

  useLayoutEffect(() => {
    if (!enabled) {
      // Only clear when content changed; otherwise we'd clear when isStable flips (infinite loop).
      const contentChanged = contentKey !== prevContentKeyRef.current;
      if (contentChanged) {
        prevContentKeyRef.current = contentKey;
        setSplitableByKey(null);
      }
      return;
    }

    const refs = sectionRefsRef.current;
    if (!refs?.length) {
      setSplitableByKey(null);
      return;
    }

    // Only read from DOM when we have one block per section (no splits yet). Once we have
    // splits, ref indices no longer match section indices, so we keep previous splitable data.
    const isFullRender =
      sectionCountPerColumn.length > 0 &&
      refs.every((colRefs, col) => (colRefs?.length ?? 0) === (sectionCountPerColumn[col] ?? 0));
    if (!isFullRender) {
      return;
    }

    // Defer read to after layout/paint so getBoundingClientRect returns final heights.
    let cancelled = false;
    let innerRafId = 0;
    const rafId = requestAnimationFrame(() => {
      innerRafId = requestAnimationFrame(() => {
        if (cancelled) return;
        const currentRefs = sectionRefsRef.current;
        if (!currentRefs?.length) return;
        const stillFull =
          sectionCountPerColumn.length > 0 &&
          currentRefs.every(
            (colRefs, col) => (colRefs?.length ?? 0) === (sectionCountPerColumn[col] ?? 0),
          );
        if (!stillFull) return;

        const toLogical = (v: number) => (scale !== 1 ? v / scale : v);
        const map = splitableSections.reduce<Map<string, SplitableHeights>>(
          (acc, [col, sectionIndex]) => {
            const el = currentRefs[col]?.[sectionIndex];
            if (!el?.querySelector?.('[data-splitable-header]')) return acc;
            const data = readSplitableHeights(el as HTMLElement, gapPx);
            if (data)
              acc.set(`${col}-${sectionIndex}`, {
                headerHeight: toLogical(data.headerHeight),
                itemHeights: data.itemHeights.map(toLogical),
                itemGapPx: toLogical(data.itemGapPx),
                footerHeight: toLogical(data.footerHeight),
              });
            return acc;
          },
          new Map(),
        );
        prevContentKeyRef.current = contentKey;
        setSplitableByKey(map);
      });
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
      if (innerRafId) cancelAnimationFrame(innerRafId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, contentKey, gapPx, splitableSections, sectionCountPerColumn, scale]);

  return splitableByKey;
}
